<script setup>
import { storeToRefs } from 'pinia';
import MainEvents from './MainEvents.vue';
import HorizontalScroller from '@/components/App/HorizontalScroller.vue';
import { ref, watchEffect, watch, computed } from 'vue';
import { useFixturesStore } from '@/stores/fixtures';
import { useI18n } from 'vue-i18n';
import { useVisibleItems } from '@/composables/useVisibleItems';

const { t, te } = useI18n()
const fixtures = useFixturesStore()
const { getMainCompetitions: mainCompetitions , eventsByCompetition } = storeToRefs(fixtures)

const selectedCompetition = ref(null)

// selections per event
const leagueSelectionsMap = ref({})

const loadVisible = async (ids) => {
  if (!ids.length) return
  const res = await fixtures.fetchSelections(ids, "pre", [20890])
  for (const ev of res) {
    leagueSelectionsMap.value[ev.fi] = ev.m || []
  }
}

const { observe, reset } = useVisibleItems(loadVisible, { selector: '.league-event-item' })

watchEffect(() => {
  if (mainCompetitions.value.length && !selectedCompetition.value) {
    selectedCompetition.value = mainCompetitions.value[0]
  }
})

watch(selectedCompetition, () => {
  reset()
  observe()
}, { immediate: true })
const competitionName = computed(() => (item) => {
  return te(`competitions.${item.ci}`)
    ? t(`competitions.${item.ci}`)
    : item.cn
})

</script>

<template>
  <section class="block px-3">
    <div class="mb-4">
      <p class="font-futura text-base text-[var(--color-primary4)] uppercase font-bold">{{ $t('app.32') }}</p>
    </div>

    <!-- Horizontal scroller -->
    <div v-if="mainCompetitions.length" class="relative">
      <HorizontalScroller
        id="mainl-scroller"
        :items="mainCompetitions"
        :active-id="selectedCompetition?.ci"
      >
        <template #item="{ item }">
          <div class="league-card-wrapper" @click="selectedCompetition = item">
            <div
              class="league-tab"
              :class="selectedCompetition?.ci == item.ci ? 'border border-[var(--color-primary4)]' : 'league-tab-not'"
            >
              <div class="league-logo">
                <img :src="item?.logo" />
              </div>
              <div class="league-name">
                  <span>{{ competitionName(item) }}</span>
              </div>
              <div class="league-icn" v-if="selectedCompetition?.ci !== item.ci">
                <img src="/sport/1.svg" style="opacity: .5; width: 23px; height: 23px;" />
              </div>
            </div>
          </div>
        </template>
      </HorizontalScroller>
    </div>

    <!-- Events for selected competition -->
    <div v-if="selectedCompetition">
      <MainEvents
        :events="eventsByCompetition(selectedCompetition?.ci).slice(0, 5)"
        :inline="false"
        :selectionsMap="leagueSelectionsMap"
        :live="false"
        :market="20890"
        item-class="league-event-item"
      />  

      <RouterLink 
        :to="`ps/${eventsByCompetition(selectedCompetition?.ci)[0].si}/l/${eventsByCompetition(selectedCompetition?.ci)[0].ri}/${selectedCompetition.ci}`"  
        class="show-btn" 
      >
        <span>{{ $t('app.33') }}</span>
        <div class="mgc-Chevron " />
      </RouterLink>
    </div>
  </section>
</template>

<style scoped>
.show-btn {
    @apply pt-2 text-center flex items-center justify-center gap-1 text-[var(--color-primary6)] hover:text-[var(--color-primary4)] text-sm font-bold
}
.mgc-Chevron {
    mask-image: url('/chevron-right.svg');
    background-color: var(--color-primary6);
    mask-size: 16px 16px;
    width: 16px;
    height: 16px;
}
.mgc-Chevron:hover {
    background-color: var(--color-primary4);
}
.league-card-wrapper {
    padding: 10px 4px;
    display: flex;
    height: 120px;
}
.league-tab-not{
    border-right: 1px solid var(--color-white7);
    border-bottom: 1px solid var(--color-white7);
}
.league-tab{
    background-color: var(--color-white7);
    color: white;
    height: 80%;
    border-radius: 10px;
    min-width: 100px;
    width: min-content;
    padding: 5px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    cursor: pointer;
    transition: background-color .2s ease-in, color .1s ease-in;
    position: relative; 
    overflow: visible;  
}
.league-logo {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 5px;
    top: -17px;
}
.league-logo img{
    width: 35px;
    height: 35px;
}
.league-name{
    width: 100%;
    height: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-bottom: 15px;
}
.league-name span{
    font-size: 13px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    max-width: 100%;
    cursor: pointer;
    margin: 0 5px;
    font-weight: 600;
    white-space: normal; 
}
.league-icn {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.league-icn img {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 0px;
    bottom: -5px;
    width: 17px;
    height: 17px;
    border-radius: 50%;
}
</style>
